<template lang="pug">
    div(ref="_in",
        class="_sli",
        :class="{ _range: range, _slot: !!$slots.default, _drag: !!bound }",
        :style="{ width, height }",
        :id="id",
        :disabled="disabled",
        :inert="inert",
        :tabindex="inert ? -1 : tabindex",
        v-on="__on",
        @keydown="_keypress",
        @touchstart="_touchstart",
        @touchend="_touchend")
        div(ref="track", class="_trk")
        div(ref="thumb0", class="_thm0", :class="{ _zero: ivalue == min }")
        div(ref="thumb1", class="_thm1", v-if="range")
        //-
            Render slot as the background of the slider
        //
        slot
</template>

<script>
    import entry from './ib-form-entry.vue';
    import telemetry from '../private/telemetry'

    /**
     * A slider input control that can select a single numeric value or a range.
     */
    export default {
        name: 'ib-slider',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
             * If true, selection is a range, and value is a two-element array.
             */
            range: Boolean,
            /**
             * Value of slider position in range [min..max] inclusive.
             */
            value: {
                type: [Number, Array],
                default: undefined
            },
            /**
             * Minimum value, leftmost slider position.
             */
            min: {
                type: Number,
                default: 0
            },
            /**
             * Maximum value, rightmost slider position.
             */
            max: {
                type: Number,
                default: 100
            },
            /**
             * Step separating each tick in the slider
             */
            step: {
                type: Number,
                default: 1
            },
            /**
             * Should take focus when made visible
             */
            autofocus: Boolean
        },

        data() {
            return {
                bound: null,
                thumb: 0,
                changed: false
            };
        },

        methods: {
            /** @private */
            _mousedown(e) {
                if (!e.button && !this.disabled && !this.inert) {
                    const input = this.$refs._in;
                    input.focus();
                    this._start(e.clientX);
                    this._mousemove(e);
                    if (input.setCapture) {
                        input.setCapture();
                        input.addEventListener('mousemove', this._mousemove);
                    } else {
                        document.body.addEventListener('mousemove', this._mousemove, true);
                        document.body.addEventListener('mouseup', this._bodyup, true);
                        document.documentElement.addEventListener('mouseout', this._bodyout);
                    }
                } else {
                    e.preventDefault();
                }
            },

            /** @private */
            _mousemove(e) {
                this._move(e.clientX);
                e.preventDefault();
            },

            /** @private */
            _bodyout(e) {
                if (e.target === document.documentElement) {
                    this._bodyup();
                }
            },

            /** @private */
            _bodyup() {
                document.body.removeEventListener('mousemove', this._mousemove, true);
                document.body.removeEventListener('mouseup', this._bodyup, true);
                document.documentElement.removeEventListener('mouseout', this._bodyout);
                this._end();
            },

            /** @private */
            _mouseup(e) {
                const input = this.$refs._in;
                if (input.releaseCapture) {
                    input.releaseCapture();
                    input.removeEventListener('mousemove', this._mousemove);
                }
                this._end();
            },

            /** @private */
            _touchstart(e) {
                if (!this.disabled && !this.inert) {
                    const input = this.$refs._in;
                    input.addEventListener('touchmove', this._touchmove);
                    this._start(e.touches[0].clientX);
                    this._touchmove(e);
                }
            },

            /** @private */
            _touchend(e) {
                const input = this.$refs._in;
                input.removeEventListener('touchmove', this._touchmove);
                this.bound = null;
            },

            /** @private */
            _touchmove(e) {
                this._move(e.touches[0].clientX);
                e.preventDefault();
            },

            /** @private */
            _keypress(e) {
                if (!this.disabled && !this.inert) {
                    let value;
                    switch (e.keyCode) {
                    case 37:
                    case 38:
                        if (this.range) {
                            value = [Math.max(this.min, this.ivalue[0] - this.step), this.ivalue[1]];
                        } else {
                            value = Math.max(this.min, this.ivalue - this.step);
                        }
                        break;

                    case 39:
                    case 40:
                        if (this.range) {
                            value = [Math.min(this.max, this.ivalue[1], this.ivalue[0] + this.step), this.ivalue[1]];
                        } else {
                            value = Math.min(this.max, this.ivalue + this.step);
                        }
                        break;
                    }
                    if (value != null) {
                        e.preventDefault();
                        this._set(value);
                        this.$emit('change', value);
                    }
                }
            },

            /** @private */
            _sync() {
                if (this.dirty) {
                    if (this.range) {
                        const position0 = Math.min(1, Math.max(0, (this.ivalue[0] - this.min) / ((this.max - this.min) || 1))) || 0;
                        const position1 = Math.min(1, Math.max(0, (this.ivalue[1] - this.min) / ((this.max - this.min) || 1))) || 0;
                        Object.assign(this.$refs.track.style, this.$util.transform({
                            tx: position0 * 100 + '%',
                            sx: position1 - position0
                        }));
                        this.$refs.thumb0.style.left = position0 * 100 + '%';
                        this.$refs.thumb1.style.left = position1 * 100 + '%';
                    } else {
                        const position = Math.min(1, Math.max(0, (this.ivalue - this.min) / ((this.max - this.min) || 1))) || 0;
                        Object.assign(this.$refs.track.style, this.$util.transform({
                            sx: position
                        }));
                        this.$refs.thumb0.style.left = position * 100 + '%';
                    }
                    this.dirty = false;
                }
            },

            /** @private */
            _start(x) {
                this.bound = this.$refs._in.getBoundingClientRect();
                if (this.range) {
                    if (this.ivalue[0] == this.max) {
                        this.thumb = 0;
                    } else if (this.ivalue[1] == this.min) {
                        this.thumb = 1;
                    } else {
                        const value = this.min + (x - this.bound.left) / this.bound.width * (this.max - this.min);
                        const dl = value - this.ivalue[0];
                        const dr = value - this.ivalue[1];
                        this.thumb = dl < 0 ? 0 : dr > 0 ? 1 : dl < -dr ? 0 : 1;
                    }
                }
            },

            /** @private */
            _move(x) {
                const value =
                    Math.max(this.min,
                    Math.min(this.max,
                        this.min + this.step * Math.round((x - this.bound.left) / (this.step * this.bound.width) * (this.max - this.min))));

                if (this.range) {
                    if (this.ivalue[this.thumb] != value) {
                        this.changed = true;
                        this._set(!this.thumb
                            ? [Math.min(value, this.ivalue[1]), this.ivalue[1]]
                            : [this.ivalue[0], Math.max(value, this.ivalue[0])]);
                    }
                } else {
                    if (this.ivalue != value) {
                        this.changed = true;
                        this._set(value);
                    }
                }
            },

            /** @private */
            _end() {
                this.bound = null;
                if (this.changed) {
                    this.changed = false;
                    this.$emit('change', this.ivalue);
                }
            },

            /**
             * Custom validation, due to emulated component.
             * @private
             */
            validate() {
                this.valid = !this.error;
                this.__refresherror();
                return this.valid;
            },

            getvalidity() {
                return this.error;
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._sli {
        display: inline-block;
        position: relative;
        max-width: 100%;
        width: 12em;
        padding: 6px 0 8px;
        -webkit-tap-highlight-color: transparent;
        color: @fg-accent;
        cursor: pointer !important;
        outline: none;
        &::before {
            content: '';
            position: absolute;
            width: 100%;
            height: 2px;
            background: fade(@fg, 16%);
        }
        &._drag {
            ._thm0::before,
            ._thm1::before {
                transform: scale(1.1429);
            }
        }
        &[disabled] {
            opacity: .5;
        }
        > ._trk {
            position: absolute;
            top: 6px;
            width: 100%;
            height: 2px;
            background: currentColor;
            transform-origin: left;
        }
    }

    /** Handles for standard slider */

    ._sli {
        > ._thm0,
        > ._thm1 {
            position: absolute;
            top: 0;
            &::before {
                content: '';
                position: absolute;
                left: -7px;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: currentColor;
                border: 2px solid transparent;
                will-change: transform;
                transition: transform .2s linear, background .25s linear, border-color .25s linear;
            }
            &._zero::before {
                background: @bg;
                border-color: fade(@fg, 25%);
            }
        }
    }

    /** Handles for slider with slot content */

    ._sli._slot {
        width: auto;
        height: auto;
        max-width: none;
        > ._trk {
            height: calc(~'100% - 14px');
            opacity: .16;
        }
        > ._thm0,
        > ._thm1 {
            top: 0;
            height: calc(~'100% - 2px');
            &::before {
                top: 6px;
                height: calc(~'100% - 12px');
            }
            &::after {
                content: '';
                position: absolute;
                top: 9px;
                width: 4px;
                height: calc(~'100% - 18px');
                border-left: 1px solid rgba(255,255,255,.5);
                border-right: 1px solid rgba(255,255,255,.5);
            }
        }
        > ._thm0 {
            border-right: 1px solid currentColor;
            &::before {
                left: -5px;
                width: 10px;
                border-radius: 2px;
            }
            &::after {
                left: -2px;
            }
        }
        > ._thm1 {
            border-left: 1px solid currentColor;
            &::before {
                left: 0;
                width: 9px;
                border-radius: 0 2px 2px 0;
            }
            &::after {
                left: 2px;
            }
        }
        &._range {
            > ._thm0 {
                &::before {
                    left: auto;
                    right: 0;
                    width: 9px;
                    border-radius: 2px 0 0 2px;
                }
                &::after {
                    left: -6px;
                }
            }
        }
        &._drag {
            > ._thm0::before,
            > ._thm1::before {
                transform: none;
            }
        }
        &::before {
            display: none;
        }
    }
</style>

<docs>
    Examples:

    ```jsx
        new Vue({
            data() {
                return {
                    val1: 5,
                    val2: [2, 8],
                    val3: 5,
                    val4: 5,
                    val5: [2, 8]
                }
            },
            template:
                `<ib-context>
                    <p>Basic Example: {{val1}}</p>
                    <p><ib-slider :min="0" :max="10" :step="1" v-model="val1"/></p>
                    <p class="before-lg">Range Example: {{val2}}</p>
                    <p><ib-slider :min="0" :max="10" :step="1" range v-model="val2"/></p>
                    <p class="before-lg">Color Example: {{val3}}</p>
                    <p><ib-slider :min="0" :max="10" :step="1" class="negative" v-model="val3"/></p>
                    <p class="before-lg">Disabled: {{val4}}</p>
                    <p><ib-slider :min="0" :max="10" :step="1" disabled v-model="val4"/></p>
                    <p class="before-lg">Range and Background: {{val2}}</p>
                    <p><ib-slider :min="0" :max="10" :step="1" range v-model="val5">
                        <div :style="{ width:'160px', height:'24px', background:'#eee' }"></div>
                    </ib-slider></p>
                </ib-context>`
        });
    ```
</docs>
